import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import { CalendarClock, Mail, Search, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getBulkSchedulePendingInvites, sendBulkInviteReminders } from '../../../api/client';
import useDebounce from '../../../hooks/useDebounce';
import BulkScheduleJdSelect from './BulkScheduleJdSelect';

const FONT = { fontFamily: "'Poppins', sans-serif" };

const outlinedInputSx = {
  borderRadius: '12px',
  fontFamily: FONT.fontFamily,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0ea5e9',
  },
};

const buttonSx = {
  ...FONT,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '12px',
};

const skipReasonTitle = (row, t) => {
  if (row.skip_reason === 'cooldown') {
    if (row.can_remind_at) {
      return t('bulkSchedule.cooldownUntilTooltip', { when: formatDateTime(row.can_remind_at) });
    }
    return t('bulkSchedule.cooldownTooltip');
  }
  if (row.skip_reason === 'expired') return t('bulkSchedule.expiredTooltip');
  if (row.skip_reason === 'used') return t('bulkSchedule.usedTooltip');
  return null;
};

const formatDateTime = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toNumericIds = (ids) => Array.from(ids || []).map((id) => Number(id));

const normalizeSelection = (newSelectionModel, rows) => {
  if (Array.isArray(newSelectionModel)) {
    return toNumericIds(newSelectionModel);
  }
  if (newSelectionModel && typeof newSelectionModel === 'object') {
    if (newSelectionModel.type === 'include') {
      return toNumericIds(newSelectionModel.ids);
    }
    if (newSelectionModel.type === 'exclude') {
      const excludedIds = new Set(toNumericIds(newSelectionModel.ids));
      return rows.map((row) => row.id).filter((id) => !excludedIds.has(Number(id)));
    }
  }
  return [];
};

const BatchInviteTable = ({
  batch,
  columns,
  gridKey,
  sending,
  onRequestSend,
  t,
}) => {
  const [selectedInviteIds, setSelectedInviteIds] = useState([]);
  const invites = useMemo(
    () => (batch.invites || []).map((row) => ({ ...row, id: row.invite_id })),
    [batch.invites]
  );
  const eligibleIds = useMemo(
    () => invites.filter((row) => row.can_remind).map((row) => row.id),
    [invites]
  );
  const eligibleSelectedIds = selectedInviteIds.filter((id) => eligibleIds.includes(id));
  const nextRemindAt = batch.next_remind_at || invites.find((row) => row.can_remind_at)?.can_remind_at;
  const gridHeight = Math.min(Math.max(invites.length, 1) * 52 + 56, 420);

  useEffect(() => {
    setSelectedInviteIds([]);
  }, [batch.key, gridKey]);

  const handleSelectionChange = (newSelectionModel) => {
    setSelectedInviteIds(normalizeSelection(newSelectionModel, invites));
  };

  const handleSelectAllEligible = (event) => {
    event.stopPropagation();
    setSelectedInviteIds(eligibleIds);
  };

  const handleRequestSendClick = (event) => {
    event.stopPropagation();
    onRequestSend(eligibleSelectedIds, batch.key);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, justifyContent: 'flex-end', alignItems: 'center' }}>
        {eligibleIds.length === 0 && nextRemindAt ? (
          <Typography variant="body2" sx={{ ...FONT, color: '#64748b', mr: 'auto' }}>
            {t('bulkSchedule.nextRemindAvailable', { when: formatDateTime(nextRemindAt) })}
          </Typography>
        ) : eligibleSelectedIds.length > 0 ? (
          <Typography variant="body2" sx={{ ...FONT, color: '#64748b', mr: 'auto' }}>
            {t('bulkSchedule.selectedCount', { count: eligibleSelectedIds.length })}
          </Typography>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          disabled={eligibleIds.length === 0 || sending}
          onClick={handleSelectAllEligible}
          sx={{ ...buttonSx, borderColor: '#cbd5e1', color: '#334155' }}
        >
          {t('bulkSchedule.selectAllEligible')}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Mail size={14} />}
          disabled={eligibleSelectedIds.length === 0 || sending}
          onClick={handleRequestSendClick}
          sx={{
            ...buttonSx,
            bgcolor: '#0ea5e9',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0284c7', boxShadow: 'none' },
          }}
        >
          {t('bulkSchedule.sendReminder')}
        </Button>
      </Box>
      <Box sx={{ height: gridHeight, width: '100%' }}>
        <DataGrid
          rows={invites}
          columns={columns}
          checkboxSelection
          hideFooter
          disableColumnMenu
          rowSelectionModel={{ type: 'include', ids: new Set(selectedInviteIds) }}
          isRowSelectable={(params) => Boolean(params.row.can_remind)}
          onRowSelectionModelChange={handleSelectionChange}
          getRowClassName={(params) => (params.row.can_remind ? '' : 'opacity-60')}
          sx={{
            ...FONT,
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            '& .MuiDataGrid-columnHeaders': {
              ...FONT,
              backgroundColor: '#f8fafc',
              color: '#475569',
              fontWeight: 600,
              fontSize: '0.8125rem',
            },
            '& .MuiDataGrid-cell': {
              ...FONT,
              fontSize: '0.875rem',
              color: '#0f172a',
            },
            '& .MuiDataGrid-row.opacity-60': { opacity: 0.55 },
          }}
        />
      </Box>
    </Box>
  );
};

const BulkScheduleFollowUp = ({
  jdOptions = [],
  selectedJd,
  onJdChange,
  onJdInputChange,
  searchTerm = '',
  loadingJds = false,
}) => {
  const { t } = useTranslation();
  const selectedJdTitle = jdOptions.find((option) => String(option.id) === String(selectedJd))?.title || '';

  const [batches, setBatches] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingInviteIds, setPendingInviteIds] = useState([]);
  const [pendingBatchKey, setPendingBatchKey] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [gridKey, setGridKey] = useState(0);

  const debouncedCandidateSearch = useDebounce(candidateSearch, 400);
  const totalInvites = batches.reduce((sum, batch) => sum + (batch.invite_count || 0), 0);

  const fetchInvites = useCallback(async (jdId, search = '') => {
    if (!jdId) {
      setBatches([]);
      return;
    }
    setLoadingInvites(true);
    try {
      const data = await getBulkSchedulePendingInvites(jdId, { search });
      setBatches(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      console.error('Failed to load pending invites', err);
      toast.error(err.response?.data?.error || t('bulkSchedule.loadPendingError'));
      setBatches([]);
    } finally {
      setLoadingInvites(false);
    }
  }, [t]);

  useEffect(() => {
    if (!selectedJd) {
      setBatches([]);
      setExpandedKeys(new Set());
      return;
    }
    fetchInvites(selectedJd, debouncedCandidateSearch);
  }, [selectedJd, debouncedCandidateSearch, fetchInvites]);

  const columns = useMemo(() => [
    {
      field: 'candidate_name',
      headerName: t('bulkSchedule.candidateName'),
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const reasonTitle = skipReasonTitle(params.row, t);
        const name = params.value || t('bulkSchedule.nA');
        if (!reasonTitle) return name;
        return (
          <Tooltip title={reasonTitle}>
            <span>{name}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'candidate_email',
      headerName: t('bulkSchedule.candidateEmail'),
      flex: 1.2,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'created_at',
      headerName: t('bulkSchedule.invitedAt'),
      width: 170,
      sortable: false,
      valueFormatter: (value) => (value ? formatDateTime(value) : t('bulkSchedule.nA')),
    },
    {
      field: 'last_reminder_sent',
      headerName: t('bulkSchedule.lastReminder'),
      width: 170,
      sortable: false,
      valueFormatter: (value) => (value ? formatDateTime(value) : t('bulkSchedule.never')),
    },
    {
      field: 'reminder_count',
      headerName: t('bulkSchedule.remindersSent'),
      width: 140,
      sortable: true,
    },
    {
      field: 'days_remaining',
      headerName: t('bulkSchedule.daysRemaining'),
      width: 110,
      sortable: true,
    },
  ], [t]);

  const handleRequestSend = (inviteIds, batchKey) => {
    setPendingInviteIds(inviteIds);
    setPendingBatchKey(batchKey || null);
    setConfirmOpen(true);
  };

  const refreshUntilReminded = async (jdId, search, inviteIds) => {
    const wanted = new Set(inviteIds);
    for (let attempt = 0; attempt < 8; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
      const data = await getBulkSchedulePendingInvites(jdId, { search });
      const results = Array.isArray(data?.results) ? data.results : [];
      setBatches(results);
      const matched = results.flatMap((batch) => batch.invites || []).filter((row) => wanted.has(row.invite_id));
      if (matched.length > 0 && matched.every((row) => Boolean(row.last_reminder_sent))) {
        return true;
      }
    }
    return false;
  };

  const handleSend = async () => {
    if (!selectedJd || pendingInviteIds.length === 0) return;
    const sentInviteIds = [...pendingInviteIds];
    const sentBatchKey = pendingBatchKey;
    setSending(true);
    try {
      const data = await sendBulkInviteReminders(selectedJd, sentInviteIds);
      const queued = data?.queued ?? 0;
      const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;
      setConfirmOpen(false);
      setPendingInviteIds([]);
      setGridKey((prev) => prev + 1);
      if (sentBatchKey) {
        setExpandedKeys(new Set([sentBatchKey]));
      }
      const updated = await refreshUntilReminded(selectedJd, debouncedCandidateSearch, sentInviteIds);
      const sentMessage = queued === 1
        ? t('bulkSchedule.sentCountOne')
        : t('bulkSchedule.sentCount', { count: queued });
      toast.success(
        `${sentMessage} ${skipped ? t('bulkSchedule.skippedCount', { count: skipped }) : ''}`.trim()
      );
      if (!updated) {
        toast.info(t('bulkSchedule.reminderHistoryPending'));
      }
    } catch (err) {
      console.error('Failed to send reminders', err);
      toast.error(err.response?.data?.error || t('bulkSchedule.sendReminderError'));
    } finally {
      setSending(false);
    }
  };

  const batchTitle = (batch) => {
    if (!batch.is_bulk_batch && batch.key === 'other') {
      return t('bulkSchedule.otherPendingInvites');
    }
    return t('bulkSchedule.batchScheduled', { when: formatDateTime(batch.created_at) });
  };

  return (
    <Box sx={{ py: 4, ...FONT }}>
      <Typography variant="h6" sx={{ ...FONT, fontWeight: 600, color: '#0f172a', mb: 2 }}>
        {t('bulkSchedule.pendingInvites')}
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <BulkScheduleJdSelect
          jdOptions={jdOptions}
          selectedJd={selectedJd}
          onChange={onJdChange}
          onInputChange={onJdInputChange}
          searchTerm={searchTerm}
          loadingJds={loadingJds}
          sx={{
            ...FONT,
            '& .MuiInputBase-root': FONT,
            '& .MuiInputLabel-root': FONT,
            '& .MuiInputBase-input': FONT,
          }}
        />
        {selectedJd && (
          <TextField
            fullWidth
            label={t('bulkSchedule.searchCandidates')}
            placeholder={t('bulkSchedule.searchCandidatesPlaceholder')}
            value={candidateSearch}
            onChange={(e) => setCandidateSearch(e.target.value)}
            sx={{ mt: 2.5 }}
            InputLabelProps={{ sx: FONT }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#94a3b8" />
                </InputAdornment>
              ),
              sx: outlinedInputSx,
            }}
          />
        )}
      </Paper>

      {!selectedJd && (
        <Paper sx={{ p: 5, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', textAlign: 'center' }}>
          <Typography sx={{ ...FONT, color: '#64748b' }}>
            {t('bulkSchedule.selectJdFirst')}
          </Typography>
        </Paper>
      )}

      {selectedJd && (
        <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          {loadingInvites ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : batches.length === 0 ? (
            <Typography sx={{ ...FONT, color: '#64748b', py: 4, textAlign: 'center' }}>
              {debouncedCandidateSearch
                ? t('bulkSchedule.noMatchingInvites')
                : t('bulkSchedule.noPendingInvites')}
            </Typography>
          ) : (
            <>
              <Typography sx={{ ...FONT, fontSize: '0.8125rem', fontWeight: 600, color: '#64748b', mb: 2 }}>
                {t('bulkSchedule.batchSummary', { batches: batches.length, invites: totalInvites })}
              </Typography>
              {batches.map((batch, index) => (
                <Accordion
                  key={batch.key || `batch-${index}`}
                  expanded={expandedKeys.has(batch.key)}
                  onChange={(_, isExpanded) => {
                    setExpandedKeys((prev) => {
                      const next = new Set(prev);
                      if (isExpanded) next.add(batch.key);
                      else next.delete(batch.key);
                      return next;
                    });
                  }}
                  disableGutters
                  sx={{
                    mb: 1.5,
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px !important',
                    boxShadow: 'none',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: '#cbd5e1',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                    sx={{
                      ...FONT,
                      px: 2,
                      minHeight: 64,
                      '& .MuiAccordionSummary-content': { my: 1.25, alignItems: 'center' },
                    }}
                  >
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(14, 165, 233, 0.1)',
                      color: '#0284c7',
                      flexShrink: 0,
                      mr: 2,
                    }}>
                      {batch.key === 'other' ? <UserRound size={18} /> : <CalendarClock size={18} />}
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography sx={{ ...FONT, fontWeight: 600, fontSize: '0.9375rem', color: '#0f172a' }}>
                        {batchTitle(batch)}
                      </Typography>
                      {batch.created_by_name ? (
                        <Typography sx={{ ...FONT, fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>
                          {t('bulkSchedule.scheduledBy', { name: batch.created_by_name })}
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip
                      label={t('bulkSchedule.batchPendingCount', { count: batch.invite_count })}
                      size="small"
                      sx={{
                        ...FONT,
                        mr: 1,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: '#0369a1',
                        background: 'rgba(14, 165, 233, 0.08)',
                        border: '1px solid rgba(14, 165, 233, 0.16)',
                      }}
                    />
                    {batch.last_reminder_sent ? (
                      <Chip
                        label={t('bulkSchedule.lastReminderAt', { when: formatDateTime(batch.last_reminder_sent) })}
                        size="small"
                        sx={{
                          ...FONT,
                          mr: 1,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color: '#166534',
                          background: 'rgba(22, 163, 74, 0.08)',
                          border: '1px solid rgba(22, 163, 74, 0.16)',
                        }}
                      />
                    ) : null}
                    {batch.next_remind_at && !batch.invites?.some((invite) => invite.can_remind) ? (
                      <Chip
                        label={t('bulkSchedule.nextRemindAvailable', { when: formatDateTime(batch.next_remind_at) })}
                        size="small"
                        sx={{
                          ...FONT,
                          mr: 1,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color: '#92400e',
                          background: 'rgba(245, 158, 11, 0.10)',
                          border: '1px solid rgba(245, 158, 11, 0.18)',
                        }}
                      />
                    ) : null}
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                    <BatchInviteTable
                      batch={batch}
                      columns={columns}
                      gridKey={gridKey}
                      sending={sending}
                      onRequestSend={handleRequestSend}
                      t={t}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </Paper>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => !sending && setConfirmOpen(false)}
        PaperProps={{
          sx: {
            ...FONT,
            width: { xs: '90%', sm: 450 },
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15,23,42,0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            ...FONT,
            m: 0,
            py: 2,
            px: 2.5,
            fontWeight: 600,
            fontSize: '1.05rem',
            color: 'white',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
          }}
        >
          {t('bulkSchedule.sendReminder')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '20px !important', pb: 1 }}>
          <DialogContentText sx={{ ...FONT, color: '#475569', fontSize: '0.9rem', lineHeight: 1.6 }}>
            {t('bulkSchedule.sendReminderConfirm', {
              count: pendingInviteIds.length,
              title: selectedJdTitle,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={sending}
            variant="outlined"
            sx={{
              ...buttonSx,
              color: '#64748b',
              borderColor: '#e2e8f0',
              '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' },
            }}
          >
            {t('bulkSchedule.cancel')}
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={sending}
            sx={{
              ...buttonSx,
              color: 'white',
              background: 'linear-gradient(135deg, #0ea5e9, #d946ef)',
              boxShadow: 'none',
              '&:hover': {
                opacity: 0.9,
                background: 'linear-gradient(135deg, #0ea5e9, #d946ef)',
                boxShadow: 'none',
              },
            }}
          >
            {sending ? t('bulkSchedule.sendingReminders') : t('bulkSchedule.confirmSend')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkScheduleFollowUp;
